import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getApiUrl } from '../config/api';
import { 
  ArrowRightIcon,
  PlayCircleIcon,
  CheckCircleIcon,
  UsersIcon,
  AcademicCapIcon,
  TrophyIcon,
  SparklesIcon,
  BookOpenIcon,
  BeakerIcon,
  HeartIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  BoltIcon,
  LightBulbIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PhoneIcon,
  MapPinIcon,
  EnvelopeIcon,
  ClockIcon,
  StarIcon,
  BanknotesIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  ScissorsIcon,
  HomeModernIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../contexts/SiteBrandingContext';
import TranslatableText from '../components/TranslatableText';
import ImageSlider from '../components/ImageSlider';

const Home = () => {
  const { isDark } = useTheme();
  const { siteBranding } = useSiteBranding();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [stats, setStats] = useState({
    students: 0,
    teachers: 0,
    awards: 0,
    results: 0
  });
  const [originalStats, setOriginalStats] = useState({
    students: 0,
    teachers: 0,
    awards: 0,
    results: 0
  });
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [featuredAchievements, setFeaturedAchievements] = useState([]);
  const [urgentNews, setUrgentNews] = useState([]);
  const [principalData, setPrincipalData] = useState({
    name: "Smt. Sunita",
    qualification: "M.Ed., Ph.D. in Education",
    experience: "25+ years in educational leadership",
    message: "Welcome to PM SHRI GGSSS FARMANA (44337). As your principal, I am committed to providing quality education and fostering an environment where every student can achieve their full potential.",
    email: "principal@pmshriggsssfarmana.edu.in",
    phone: "9255429697",
    image_base64: null,
    image_url: null // Removed default Unsplash URL
  });
  const [dynamicContent, setDynamicContent] = useState({
    faqs: [],
    subjects: [],
    downloadLinks: []
  });
  const [loading, setLoading] = useState(true);

  // Quick Links Data
  const quickLinks = [
    { name: 'SHIKSHA SARTHI', url: 'http://schooleducationharyana.gov.in/Children/Shiksha-Saarthi', icon: BookOpenIcon },
    { name: 'CBSE', url: 'https://www.cbse.nic.in/', icon: AcademicCapIcon },
    { name: 'NCERT', url: 'https://www.ncert.nic.in/', icon: DocumentTextIcon },
    { name: 'INSPIRE REGISTRATION', url: 'http://www.inspireawards-dst.gov.in/UserP/CheckUDISECodeNewSchoolRegistration.aspx', icon: StarIcon },
    { name: 'BSEH BHIWANI', url: 'https://bseh.org.in/home', icon: BuildingLibraryIcon },
    { name: 'FEE STRUCTURE', url: '/fee-structure', icon: BanknotesIcon }
  ];

  // Academic Streams - Updated for Classes VI-XII
  const academicStreams = [
    {
      name: 'Science Stream (XI-XII)',
      description: 'Physics, Chemistry, Biology, Mathematics with cutting-edge laboratory facilities',
      icon: BeakerIcon,
      subjects: ['Physics', 'Chemistry', 'Biology', 'Mathematics', 'English', 'Physical Education and Sports','Computer Science']
    },
    {
      name: 'Commerce Stream (XI-XII)', 
      description: 'Business Studies, Economics, Accountancy with practical business exposure',
      icon: CurrencyDollarIcon,
      subjects: ['Business Studies', 'Economics', 'Accountancy', 'Mathematics', 'English', 'Physical Education and Sports']
    },
    {
      name: 'Humanities Stream (XI-XII)',
      description: 'History, Geography, Political Science, Psychology with comprehensive learning',
      icon: BookOpenIcon,
      subjects: ['History', 'Geography', 'Political Science', 'Hindi', ,'Beauty and Wellness', 'English', 'Physical Education and Sports','Home Science','Computer Science','Sanskrit','Fine Arts']
    }
  ];

  // Laboratory Facilities
  const laboratories = [
    { name: 'Physics Lab', description: 'Modern equipment for practical physics experiments', icon: BoltIcon, image: '/school-images/Physics-lab.png' },
    { name: 'Chemistry Lab', description: 'Well-equipped for chemical analysis and experiments', icon: BeakerIcon, image: '/school-images/Chemistry-lab.jpeg' },
    { name: 'Biology Lab', description: 'Advanced microscopy and biological specimens', icon: HeartIcon, image: '/school-images/Biology-lab.jpeg' },
    { name: 'Mathematics Lab', description: 'Interactive tools for mathematical concepts', icon: AcademicCapIcon, image: '/school-images/Mathematics-lab.jpeg' },
    { name: 'ICT Lab', description: 'Latest computers with high-speed internet', icon: ComputerDesktopIcon, image: '/school-images/Computer-lab.png' },
    { name: 'Beauty & Wellness Lab', description: 'Vocational training in beauty and wellness', icon: ScissorsIcon, image: '/school-images/Beauty-lab.png' },
    { name: 'Home Science Lab', description: 'Equipped for training in nutrition, cooking, and home management', icon: HomeModernIcon, image: '/school-images/HomeScience-lab.jpeg' },
    { name: 'Language Lab', description: 'Multi-media language learning facilities', icon: GlobeAltIcon, image: '/school-images/Language-lab.jpeg' }
  ];

  // Fee Structure - Updated for Classes VI-XII only
  const feeStructure = [
    { class: 'Classes VI-VIII', admission: '₹0', monthly: '₹0', note: 'Middle School' },
    { class: 'Classes IX-X', admission: '₹5', monthly: '₹25', note: 'Secondary' },
    { class: 'Classes XI-XII (Arts)', admission: '₹20', monthly: '₹50', note: 'Senior Secondary - Arts' },
    { class: 'Classes XI-XII (Commerce)', admission: '₹20', monthly: '₹60', note: 'Senior Secondary - Commerce' },
    { class: 'Classes XI-XII (Science)', admission: '₹20', monthly: '₹75', note: 'Senior Secondary - Science' }
  ];

  // School Highlights for Hero Section
  const schoolHighlights = [
        {
      title: 'District Essay Competition',
      description: '1st Position in District Level Essay Writing Competition',
      icon: BookOpenIcon,
      color: 'from-blue-400 to-indigo-500',
      image: '/school-images/achievements1.jpg'
    },
    {
      title: 'Science Exhibition Winner',
      description: '1st Position in District Level Science Exhibition',
      icon: BeakerIcon,
      color: 'from-green-400 to-emerald-500',
      image: '/school-images/achievements2.jpg'
    },
    {
      title: 'Yuva Sansad on Commissionary Level',
      description: '3rd Position in Yuva Sansad on Commissionary Level (4 Districts)',
      icon: TrophyIcon,
      color: 'from-yellow-400 to-orange-500',
      image: '/school-images/events2.jpg'
    },
    {
      title: 'Science Exhibition 1st Position in Block Level',
      description: 'Won Multiple Prizes in Science Exhibition',
      icon: ShieldCheckIcon,
      color: 'from-purple-400 to-pink-500',
      image: '/school-images/events1.jpg'
    },
            {
      title: 'Skit on District Level',
      description: '3rd Position in Skit on District Level',
      icon: BookOpenIcon,
      color: 'from-blue-400 to-indigo-500',
      image: '/school-images/events2.jpg'
    },
    {
      title: '1st Position on District Level',
      description: '1st Position in District Level Competition',
      icon: BeakerIcon,
      color: 'from-green-400 to-emerald-500',
      image: '/school-images/sports1.jpg'
    },
    {
      title: 'Speech Recitation on Block Level',
      description: '1st Position in Speech Recitation on Block Level',
      icon: TrophyIcon,
      color: 'from-yellow-400 to-orange-500',
      image: '/school-images/sports2.jpg'
    },
    {
      title: 'Hindi Questionnaire on Block Level',
      description: '3rd Position in Hindi Questionnaire on Block Level',
      icon: ShieldCheckIcon,
      color: 'from-purple-400 to-pink-500',
      image: '/school-images/sports3.jpg'
    }
  ];

  // Hero images using actual school photos from homepage-images/hero-section
  const [heroImages, setHeroImages] = useState([
    "/homepage-images/hero-section/image1.jpg",
    "/homepage-images/hero-section/image2.jpg", 
    "/homepage-images/hero-section/image3.jpg",
    "/homepage-images/hero-section/image4.jpg"
  ]);
  const [imageLoadError, setImageLoadError] = useState(false);

  const features = [
    {
      icon: BeakerIcon,
      title: "Modern Laboratories",
      description: "Fully equipped Physics, Chemistry, Biology, Mathematics, Computer, Beauty & Wellness, and Language labs with latest equipment",
      image: "/school-images/labs1.jpg"
    },
    {
      icon: BookOpenIcon,
      title: "Library & E-Resources",
      description: "Comprehensive collection of books, digital resources, and online learning materials for enhanced education",
      image: "/school-images/features1.jpg"
    },
    {
      icon: TrophyIcon,
      title: "Sports & Physical Education",
      description: "Multi-purpose sports ground, athletics track, and facilities for various indoor and outdoor games",
      image: "/school-images/sports1.jpg"
    },
    {
      icon: AcademicCapIcon,
      title: "Smart Classrooms",
      description: "Technology-enabled learning environment with interactive displays and digital learning tools",
      image: "/school-images/features2.jpg"
    }
  ];

  const achievements = [
    {
      icon: TrophyIcon,
      title: "Academic Excellence",
      description: "Best Principal Award 2023-24 - Block Farmana",
      color: "from-yellow-400 to-orange-500",
      image: "/school-images/awards1.jpeg"
    },
    {
      icon: StarIcon,
      title: "District Level Recognition",
      description: "1st Position in District Essay Writing Competition",
      color: "from-blue-400 to-purple-500",
      image: "/school-images/awards2.jpg"
    },
    {
      icon: BeakerIcon,
      title: "Science Excellence",
      description: "1st Position in District Level Science Exhibition",
      color: "from-green-400 to-emerald-500",
      image: "/school-images/awards3.jpg"
    },
    {
      icon: ShieldCheckIcon,
      title: "Educational Innovation",
      description: "Recognition for NEP 2020 Implementation Excellence",
      color: "from-indigo-400 to-blue-500",
      image: "/school-images/awards4.jpeg"
    },
    {
      icon: AcademicCapIcon,
      title: "Outstanding Performance",
      description: "Excellence in Student Academic Achievement",
      color: "from-purple-400 to-indigo-500",
      image: "/school-images/awards5.jpeg"
    },
    {
      icon: TrophyIcon,
      title: "Leadership Recognition",
      description: "Award for Educational Leadership and Excellence",
      color: "from-red-400 to-pink-500",
      image: "/school-images/awards6.jpeg"
    }
  ];

  const pmShriFeatures = [
    {
      icon: ShieldCheckIcon,
      title: "PM SHRI Status",
      description: "Recognized under Pradhan Mantri Schools for Rising India scheme"
    },
    {
      icon: BoltIcon,
      title: "Holistic Development",
      description: "Focus on academic excellence, life skills, and character building"
    },
    {
      icon: LightBulbIcon,
      title: "Innovation & Research",
      description: "Encouraging scientific temper and innovative thinking among students"
    },
    {
      icon: HeartIcon,
      title: "Community Service",
      description: "Active participation in social service and environmental conservation"
    }
  ];

  const learningBeyondClassrooms = [
    {
      name: "National Cadet Corps (NCC)",
      description: "Building discipline, leadership, and patriotism",
      icon: ShieldCheckIcon
    },
    {
      name: "National Service Scheme (NSS)",
      description: "Community service and social awareness programs",
      icon: HeartIcon
    },
    {
      name: "Youth & Eco Club",
      description: "Environmental conservation and sustainability initiatives",
      icon: GlobeAltIcon
    },
    {
      name: "Legal Literacy Club",
      description: "Understanding rights, duties, and legal awareness",
      icon: BookOpenIcon
    },
    {
      name: "BIS Club",
      description: "Quality standards and excellence in education",
      icon: TrophyIcon
    },
    {
      name: "Digital Learning Programs",
      description: "Digital Mela, Buniyaad, Super 100 initiatives",
      icon: BoltIcon
    }
  ];

  // Preload critical images for faster loading
  useEffect(() => {
    // Images are already set in state, no need to load them dynamically
  }, []);

  // Preload critical images for faster loading
  useEffect(() => {
    if (heroImages.length > 0) {
      // Preload only the first image for instant display
      const img = new Image();
      img.src = heroImages[0];
      img.loading = 'eager';
      img.decoding = 'async';
    }
  }, [heroImages]);

  useEffect(() => {
    fetchDynamicData();
    
    // Optimized image rotation with preloading
    if (heroImages.length > 0) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prevIndex) => {
          const nextIndex = prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1;
          
          // Preload next image for smooth transition
          const nextImageIndex = nextIndex === heroImages.length - 1 ? 0 : nextIndex + 1;
          if (heroImages[nextImageIndex]) {
            const img = new Image();
            img.src = heroImages[nextImageIndex];
          }
          
          return nextIndex;
        });
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [heroImages]);

  const fetchDynamicData = async () => {
    try {
      // Fetch site statistics
      const statsResponse = await fetch(getApiUrl('/site/statistics'));
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setOriginalStats({
          students: statsData.students || 600,
          teachers: statsData.teachers || 35,
          awards: statsData.awards || 15,
          results: statsData.results || 98
        });
      } else {
        setOriginalStats({
          students: 600,
          teachers: 35,
          awards: 15,
          results: 98
        });
      }

      // Fetch principal data
      try {
        const principalResponse = await fetch(getApiUrl('/site/principal'));
        if (principalResponse.ok) {
          const principalInfo = await principalResponse.json();
          setPrincipalData(prevData => ({
            ...prevData,
            ...principalInfo
          }));
        }
      } catch (principalError) {
        console.log('Using default principal data (database not connected)');
      }

      // Fetch featured events
      try {
        const eventsResponse = await fetch(getApiUrl('/events/featured'));
        console.log('Featured events API response status:', eventsResponse.status);
        if (eventsResponse.ok) {
          const events = await eventsResponse.json();
          console.log('Featured events data received:', events);
          setFeaturedEvents(events.slice(0, 3));
        } else {
          console.log('Featured events API failed with status:', eventsResponse.status);
        }
      } catch (eventsError) {
        console.error('Error fetching featured events:', eventsError);
      }

      // Fetch featured achievements
      const achievementsResponse = await fetch(getApiUrl('/achievements/featured'));
      if (achievementsResponse.ok) {
        const achievements = await achievementsResponse.json();
        setFeaturedAchievements(achievements.slice(0, 4));
      }

      // Fetch urgent news
      const newsResponse = await fetch(getApiUrl('/news/urgent'));
      if (newsResponse.ok) {
        const news = await newsResponse.json();
        setUrgentNews(news.slice(0, 3));
      }

      // Fetch FAQs
      const faqsResponse = await fetch(getApiUrl('/site/faqs'));
      if (faqsResponse.ok) {
        const faqs = await faqsResponse.json();
        setDynamicContent(prev => ({ ...prev, faqs: faqs.slice(0, 6) }));
      }

      // Fetch Subjects
      const subjectsResponse = await fetch(getApiUrl('/site/subjects'));
      if (subjectsResponse.ok) {
        const subjects = await subjectsResponse.json();
        setDynamicContent(prev => ({ ...prev, subjects: subjects.slice(0, 8) }));
      }

      // Fetch Download Links
      const downloadsResponse = await fetch(getApiUrl('/site/download-links'));
      if (downloadsResponse.ok) {
        const downloads = await downloadsResponse.json();
        setDynamicContent(prev => ({ ...prev, downloadLinks: downloads.slice(0, 5) }));
      }

    } catch (error) {
      console.error('Error fetching dynamic data:', error);
      setOriginalStats({
        students: 600,
        teachers: 35,
        awards: 15,
        results: 98
      });
    } finally {
      setLoading(false);
    }
  };

  // Animated counter effect
  useEffect(() => {
    // Only start animation once we have the original stats
    if (originalStats.students === 0 && originalStats.teachers === 0) return;
    
    const animateStats = () => {
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      
      let step = 0;
      const timer = setInterval(() => {
        const progress = step / steps;
        setStats({
          students: Math.floor(originalStats.students * progress),
          teachers: Math.floor(originalStats.teachers * progress),
          awards: Math.floor(originalStats.awards * progress),
          results: Math.floor(originalStats.results * progress)
        });
        
        step++;
        if (step > steps) {
          clearInterval(timer);
          setStats(originalStats);
        }
      }, interval);
    };

    const timer = setTimeout(animateStats, 500);
    return () => clearTimeout(timer);
  }, [originalStats]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-dark-bg' : 'bg-white'}`}>
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          {heroImages.length > 0 && (
            <motion.img 
              key={`${currentImageIndex}-${heroImages[currentImageIndex]}`}
              src={heroImages[currentImageIndex]}
              alt="PM SHRI GGSSS FARMANA - School Images" 
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.5 }}
              onError={() => {
                console.log('Image failed to load:', heroImages[currentImageIndex]);
              }}
            />
          )}
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <ShieldCheckIcon className="w-4 h-4 mr-2" />
              <TranslatableText text="Providing quality education" />
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <TranslatableText text={siteBranding?.site_name || "PM SHRI GGSSS FARMANA (44337)"} />
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              <TranslatableText text="Excellence in Education • Empowering Minds • Building Futures" />
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-cyan-300">{stats.students}+</div>
                <div className="text-blue-200"><TranslatableText text="Students" /></div>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-cyan-300">{stats.teachers}+</div>
                <div className="text-blue-200"><TranslatableText text="Teachers" /></div>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-cyan-300">{stats.awards}+</div>
                <div className="text-blue-200"><TranslatableText text="Awards" /></div>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-cyan-300">{stats.results}%</div>
                <div className="text-blue-200"><TranslatableText text="Results" /></div>
              </motion.div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link
                to="/admissions"
                className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center"
              >
                <AcademicCapIcon className="w-5 h-5 mr-2" />
                <TranslatableText text="Apply for Admission" />
              </Link>
              
              <Link
                to="/principal-message"
                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200 flex items-center justify-center"
              >
                <PlayCircleIcon className="w-5 h-5 mr-2" />
                <TranslatableText text="Principal's Message" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Image Slider Section */}
      <ImageSlider />

      {/* Principal's Message Section */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-dark-surface' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-sm font-medium mb-6">
                <UsersIcon className="w-4 h-4 mr-2" />
                <TranslatableText text="Principal's Desk" />
              </div>
              
              <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-6`}>
                <TranslatableText text="Message from the Principal" />
              </h2>
              
              <blockquote className={`text-lg italic ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-6 border-l-4 border-blue-500 pl-4`}>
                <TranslatableText text='"Education is the manifestation of the perfection already in man." — Swami Vivekanand' />
              </blockquote>
              
              <p className={`text-base ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-6 leading-relaxed`}>
                <TranslatableText text={principalData.message} />
              </p>
              
              <div className="flex items-center space-x-4">
                {principalData.image_base64 ? (
                  <img
                    src={`data:image/jpeg;base64,${principalData.image_base64}`}
                    alt={principalData.name}
                    className="rounded-full object-cover border-2 border-blue-600"
                    style={{
                      width: `${principalData.image_box_avatar_d || 64}px`,
                      height: `${principalData.image_box_avatar_d || 64}px`,
                      objectPosition: principalData.image_position_avatar || '50% 20%',
                    }}
                  />
                ) : principalData.image_url ? (
                  <img
                    src={getApiUrl(principalData.image_url, true)}
                    alt={principalData.name}
                    className="rounded-full object-cover border-2 border-blue-600"
                    style={{
                      width: `${principalData.image_box_avatar_d || 64}px`,
                      height: `${principalData.image_box_avatar_d || 64}px`,
                      objectPosition: principalData.image_position_avatar || '50% 20%',
                    }}
                    onError={(e) => {
                      // Hide image and show initials fallback on error
                      e.target.style.display = 'none';
                      if (e.target.nextElementSibling) {
                        e.target.nextElementSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                {/* Initials fallback - always present but hidden if image loads */}
                <div 
                  className={`w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white ${(principalData.image_base64 || principalData.image_url) ? 'hidden' : 'flex'}`}
                  style={{display: (!principalData.image_base64 && !principalData.image_url) ? 'flex' : 'none'}}
                >
                  <span className="text-2xl font-bold">
                    {principalData.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h4 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                    <TranslatableText text={principalData.name} />
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                    <TranslatableText text="Principal, PM SHRI GGSSS Farmana" />
                  </p>
                  {principalData.qualification && (
                    <p className={`text-xs ${isDark ? 'text-dark-text-secondary' : 'text-gray-500'} mt-1`}>
                      <TranslatableText text={principalData.qualification} />
                    </p>
                  )}
                </div>
              </div>
              
              <Link
                to="/principal-message"
                className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium mt-4"
              >
                <TranslatableText text="Read Full Message" />
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative bg-white rounded-2xl shadow-xl p-8 border">
                <div className="text-center">
                  {principalData.image_base64 ? (
                    <img
                      src={`data:image/jpeg;base64,${principalData.image_base64}`}
                      alt={principalData.name}
                      className="w-full mx-auto rounded-xl object-cover ring-1 ring-gray-200 mb-6 shadow-lg"
                      style={{
                        height: `${principalData.image_box_landscape_h || 288}px`,
                        objectPosition: principalData.image_position_landscape || principalData.image_position_hero || '50% 20%',
                      }}
                    />
                  ) : principalData.image_url ? (
                    <img
                      src={getApiUrl(principalData.image_url, true)}
                      alt={principalData.name}
                      className="w-full mx-auto rounded-xl object-cover ring-1 ring-gray-200 mb-6 shadow-lg"
                      style={{
                        height: `${principalData.image_box_landscape_h || 288}px`,
                        objectPosition: principalData.image_position_landscape || principalData.image_position_hero || '50% 20%',
                      }}
                      onError={(e) => {
                        // Hide image and show initials fallback on error
                        e.target.style.display = 'none';
                        if (e.target.nextElementSibling) {
                          e.target.nextElementSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  {/* Initials fallback - always present but hidden if image loads */}
                  <div 
                    className={`w-full h-64 mx-auto bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white text-6xl font-bold mb-6 shadow-lg ${(principalData.image_base64 || principalData.image_url) ? 'hidden' : 'flex'}`}
                    style={{display: (!principalData.image_base64 && !principalData.image_url) ? 'flex' : 'none'}}
                  >
                    {principalData.name.charAt(0)}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    <TranslatableText text={principalData.name} />
                  </h3>
                  <p className="text-gray-600 mb-4">
                    <TranslatableText text="Principal" />
                  </p>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    <TranslatableText text={`${principalData.qualification || "M.Ed., Ph.D. in Education"} | ${principalData.experience || "25+ years in educational leadership"}`} />
                  </p>
                  {(principalData.email && principalData.email !== "principal@pmshriggsssfarmana.edu.in") && (
                    <p className="text-xs text-gray-400 mt-2">
                      <TranslatableText text={principalData.email} />
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Academic Streams Section - Enhanced with Classes VI-XII Focus */}
      <section className={`py-20 ${isDark ? 'bg-dark-surface' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className={`text-4xl md:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
              <TranslatableText text="Academic Streams" />
            </h2>
            <p className={`text-xl ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-3xl mx-auto`}>
              <TranslatableText text="Comprehensive education from Classes VI to XII with specialized streams" />
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {academicStreams.map((stream, index) => (
              <motion.div
                key={index}
                className={`${isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-gray-100'} p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border group`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <stream.icon className="w-8 h-8" />
                </div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
                  <TranslatableText text={stream.name} />
                </h3>
                <p className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-6`}>
                  <TranslatableText text={stream.description} />
                </p>
                <div className="space-y-2">
                  <h4 className={`font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2`}>
                    <TranslatableText text="Subjects:" />
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {stream.subjects.map((subject, i) => (
                      <span key={i} className="inline-flex px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-medium rounded-full">
                        <TranslatableText text={subject} />
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      <section className={`py-20 ${isDark ? 'bg-dark-bg' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className={`text-4xl md:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
              <TranslatableText text="Featured Events" />
            </h2>
            <p className={`text-xl ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-3xl mx-auto`}>
              <TranslatableText text="Discover our upcoming and recent school events and activities" />
            </p>
          </motion.div>

          {featuredEvents.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredEvents.slice(0, 6).map((event, index) => (
                <motion.div
                  key={event.id}
                  className={`${isDark ? 'bg-dark-surface border-dark-border' : 'bg-white border-gray-100'} rounded-2xl shadow-lg hover:shadow-xl transition-all border overflow-hidden group`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  {event.image_url && (
                    <div className="h-48 relative overflow-hidden">
                      <img 
                        src={event.image_url} 
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center" style={{display: 'none'}}>
                        <CalendarDaysIcon className="w-16 h-16 text-white" />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                          <TranslatableText text="Featured" />
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-3`}>
                      <TranslatableText text={event.title} />
                    </h3>
                    <p className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-4 line-clamp-3`}>
                      <TranslatableText text={event.description} />
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-blue-600">
                        <CalendarDaysIcon className="w-4 h-4 mr-2" />
                        <span>{new Date(event.date).toLocaleDateString()}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPinIcon className="w-4 h-4 mr-1" />
                          <TranslatableText text={event.location} />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              className="text-center py-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <CalendarDaysIcon className={`w-16 h-16 ${isDark ? 'text-gray-500' : 'text-gray-400'} mx-auto mb-4`} />
              <h3 className={`text-xl font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2`}>
                <TranslatableText text="No Featured Events" />
              </h3>
              <p className={`${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-6`}>
                <TranslatableText text="Check back soon for upcoming events and activities" />
              </p>
              <Link
                to="/events"
                className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CalendarDaysIcon className="w-5 h-5 mr-2" />
                <TranslatableText text="View All Events" />
              </Link>
            </motion.div>
          )}

          {featuredEvents.length > 0 && (
            <motion.div
              className="text-center mt-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link
                to="/events"
                className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <CalendarDaysIcon className="w-5 h-5 mr-2" />
                <TranslatableText text="View All Events" />
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Laboratory Facilities Section */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-dark-surface' : 'bg-gradient-to-br from-gray-50 to-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-full text-sm font-medium mb-6">
              <BeakerIcon className="w-4 h-4 mr-2" />
              <TranslatableText text="Modern Infrastructure" />
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
              <TranslatableText text="Laboratory Facilities" />
            </h2>
            <p className={`text-lg sm:text-xl ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-3xl mx-auto`}>
              <TranslatableText text="State-of-the-art laboratories equipped with modern instruments and technology for hands-on learning" />
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {laboratories.map((lab, index) => (
              <motion.div
                key={index}
                className={`${isDark ? 'bg-dark-card border-dark-border hover:border-green-500/50' : 'bg-white border-gray-200 hover:border-green-200'} rounded-2xl border hover:shadow-lg transition-all overflow-hidden group`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.03 }}
              >
                {/* Lab Image */}
                <div className="h-48 bg-gradient-to-br from-green-500 to-emerald-600 relative overflow-hidden">
                  <img 
                    src={lab.image} 
                    alt={lab.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center" style={{display: 'none'}}>
                    <lab.icon className="w-16 h-16 text-white" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                      <lab.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2`}>
                    <TranslatableText text={lab.name} />
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                    <TranslatableText text={lab.description} />
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Structure Section */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-dark-bg' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-medium mb-6">
              <BanknotesIcon className="w-4 h-4 mr-2" />
              <TranslatableText text="Fee Structure" />
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
              <TranslatableText text="Affordable Education" />
            </h2>
            <p className={`text-lg sm:text-xl ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-3xl mx-auto`}>
              <TranslatableText text="Quality education at affordable rates with scholarship opportunities for deserving students" />
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="overflow-x-auto">
              <table className={`w-full ${isDark ? 'bg-dark-surface' : 'bg-white'} rounded-2xl shadow-lg border ${isDark ? 'border-dark-border' : 'border-gray-200'}`}>
                <thead className={`${isDark ? 'bg-dark-card' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
                  <tr>
                    <th className={`px-6 py-4 text-left font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                      <TranslatableText text="Class" />
                    </th>
                    <th className={`px-6 py-4 text-left font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                      <TranslatableText text="Admission Fee" />
                    </th>
                    <th className={`px-6 py-4 text-left font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                      <TranslatableText text="Monthly Fee" />
                    </th>
                    <th className={`px-6 py-4 text-left font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                      <TranslatableText text="Note" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {feeStructure.map((fee, index) => (
                    <tr key={index} className={`border-t ${isDark ? 'border-dark-border' : 'border-gray-200'}`}>
                      <td className={`px-6 py-4 font-medium ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                        <TranslatableText text={fee.class} />
                      </td>
                      <td className={`px-6 py-4 ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                        <TranslatableText text={fee.admission} />
                      </td>
                      <td className={`px-6 py-4 ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                        <TranslatableText text={fee.monthly} />
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-500'}`}>
                        <TranslatableText text={fee.note} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <motion.div 
              className={`mt-8 p-6 ${isDark ? 'bg-dark-surface border-dark-border' : 'bg-blue-50 border-blue-200'} rounded-2xl border`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h4 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2`}>
                <TranslatableText text="Scholarship & Concessions" />
              </h4>
              <p className={`text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                <TranslatableText text="Available for families with annual income ≤ ₹1.8 lakh, EWS, BPL, and meritorious students under Haryana Education Law 134-A (Classes VI-XII)" />
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-dark-surface' : 'bg-gradient-to-br from-yellow-50 to-orange-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-full text-sm font-medium mb-6">
              <TrophyIcon className="w-4 h-4 mr-2" />
              <TranslatableText text="Our Achievements" />
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
              <TranslatableText text="Awards & Recognition" />
            </h2>
            <p className={`text-lg sm:text-xl ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-3xl mx-auto`}>
              <TranslatableText text="Celebrating excellence in academics, leadership, and community service at district and state levels" />
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {schoolHighlights.map((achievement, index) => (
              <motion.div
                key={index}
                className="group"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className={`${isDark ? 'bg-dark-surface border-dark-border hover:border-yellow-500/50' : 'bg-white border-gray-100 hover:border-yellow-200'} rounded-2xl shadow-lg hover:shadow-xl transition-all border overflow-hidden group`}>
                  {/* Achievement Image */}
                  <div className="h-48 bg-gradient-to-br from-yellow-400 to-orange-500 relative overflow-hidden">
                    <img 
                      src={achievement.image} 
                      alt={achievement.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-r ${achievement.color} flex items-center justify-center`} style={{display: 'none'}}>
                      <achievement.icon className="w-16 h-16 text-white" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <div className={`w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-r ${achievement.color} rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform`}>
                        <achievement.icon className="w-6 h-6 lg:w-7 lg:h-7" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 lg:p-8">
                    <h3 className={`text-lg lg:text-xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-3`}>
                      <TranslatableText text={achievement.title} />
                    </h3>
                    <p className={`text-sm lg:text-base ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                      <TranslatableText text={achievement.description} />
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-dark-bg' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full text-sm font-medium mb-6">
              <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
              <TranslatableText text="Important Links" />
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-4`}>
              <TranslatableText text="Quick Access" />
            </h2>
            <p className={`text-lg sm:text-xl ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} max-w-3xl mx-auto`}>
              <TranslatableText text="Direct links to important educational portals and resources for students and parents" />
            </p>
          </motion.div>

          {/* Featured Events */}
          {featuredEvents.length > 0 && (
            <div className="mb-12 lg:mb-16">
              <h3 className={`text-2xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-8 text-center`}>
                Upcoming Events
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    className={`${isDark ? 'bg-dark-surface border-dark-border hover:border-blue-500/50' : 'bg-white border-gray-200 hover:border-blue-200'} p-6 rounded-2xl border hover:shadow-lg transition-all`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CalendarDaysIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2 line-clamp-2`}>
                          {event.title}
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-3 line-clamp-2`}>
                          {event.description}
                        </p>
                        <div className="text-sm text-blue-600 font-medium">
                          {new Date(event.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {event.time && ` • ${event.time}`}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Featured Achievements */}
          {featuredAchievements.length > 0 && (
            <div className="mb-12 lg:mb-16">
              <h3 className={`text-2xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-8 text-center`}>
                Recent Achievements
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                {featuredAchievements.map((achievement, index) => (
                  <motion.div
                    key={achievement.id}
                    className={`${isDark ? 'bg-dark-surface border-dark-border hover:border-yellow-500/50' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-100'} p-4 sm:p-6 rounded-2xl border hover:shadow-lg transition-all`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className="text-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <TrophyIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <h4 className={`font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2 text-xs sm:text-sm line-clamp-2`}>
                        {achievement.title}
                      </h4>
                      <p className={`text-xs ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} line-clamp-2`}>
                        {achievement.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Urgent News */}
          {urgentNews.length > 0 && (
            <div className="mb-12 lg:mb-16">
              <h3 className={`text-2xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-8 text-center`}>
                Important Announcements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {urgentNews.map((news, index) => (
                  <motion.div
                    key={news.id}
                    className={`${isDark ? 'bg-dark-surface border-dark-border hover:border-red-500/50' : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-100'} p-6 rounded-2xl border hover:shadow-lg transition-all`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <ExclamationTriangleIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`${isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800'} text-xs font-semibold px-2 py-1 rounded-full`}>
                            Urgent
                          </span>
                        </div>
                        <h4 className={`font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2 line-clamp-2`}>
                          {news.title}
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} line-clamp-3`}>
                          {news.content}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                title: "Academic Programs",
                description: "Comprehensive curriculum aligned with NEP 2020 for holistic development",
                icon: BookOpenIcon,
                href: "/academics",
                color: "from-blue-500 to-cyan-500"
              },
              {
                title: "Admissions",
                description: "Join our school family for session 2026-27 - Application now open",
                icon: AcademicCapIcon,
                href: "/admissions",
                color: "from-purple-500 to-indigo-500"
              },
              {
                title: "Events & Activities",
                description: "Vibrant campus life with cultural programs, sports events, and competitions",
                icon: SparklesIcon,
                href: "/events",
                color: "from-green-500 to-emerald-500"
              },
              {
                title: "Gallery",
                description: "Glimpses of school life, achievements, and memorable moments",
                icon: HeartIcon,
                href: "/gallery",
                color: "from-pink-500 to-rose-500"
              },
              {
                title: "Principal's Message",
                description: "Vision, mission, and leadership message from Smt. Sunita",
                icon: LightBulbIcon,
                href: "/principal-message",
                color: "from-orange-500 to-yellow-500"
              },
              {
                title: "Contact Us",
                description: "Get in touch for any queries, information, or assistance",
                icon: BoltIcon,
                href: "/contact",
                color: "from-red-500 to-pink-500"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <Link
                  to={item.href}
                  className={`block group ${isDark ? 'bg-dark-surface border-dark-border hover:border-blue-500/50' : 'bg-gradient-to-br from-gray-50 to-blue-50 border-gray-200 hover:border-blue-200'} p-6 lg:p-8 rounded-2xl border hover:shadow-xl transition-all`}
                >
                  <div className={`w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center text-white mb-4 lg:mb-6 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-6 h-6 lg:w-8 lg:h-8" />
                  </div>
                  <h3 className={`text-lg lg:text-xl font-bold ${isDark ? 'text-dark-text group-hover:text-blue-400' : 'text-gray-900 group-hover:text-blue-600'} mb-3 transition-colors`}>
                    {item.title}
                  </h3>
                  <p className={`text-sm lg:text-base ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'} mb-4`}>
                    {item.description}
                  </p>
                  <div className={`flex items-center ${isDark ? 'text-blue-400' : 'text-blue-600'} font-medium group-hover:translate-x-2 transition-transform text-sm lg:text-base`}>
                    Learn More
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;